import React, { useEffect, useState } from "react";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import CommonModal from "../../components/common/CommonModal";
import CommonFormActions from "../../components/common/CommonFormActions";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./RolesPermissions.css";

function RolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesResponse, catalogResponse] = await Promise.all([
        api.get("/roles-permissions/roles"),
        api.get("/roles-permissions/catalog"),
      ]);

      if (rolesResponse.data.success) {
        setRoles(rolesResponse.data.data || []);
      }
      if (catalogResponse.data.success) {
        setCatalog(catalogResponse.data.data || []);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load roles and permissions.",
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setFormData({
      name: "",
      description: "",
      permissions: [],
    });
    setShowModal(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name || "",
      description: role.description || "",
      permissions: role.permissions || [],
    });
    setShowModal(true);
  };

  const handleCheckboxChange = (permissionKey) => {
    setFormData((prev) => {
      const exists = prev.permissions.includes(permissionKey);
      return {
        ...prev,
        permissions: exists
          ? prev.permissions.filter((key) => key !== permissionKey)
          : [...prev.permissions, permissionKey],
      };
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Role Name Required",
        text: "Please enter a role name.",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: formData.permissions,
      };

      const response = editingRole
        ? await api.put(`/roles-permissions/roles/${editingRole.id}`, payload)
        : await api.post("/roles-permissions/roles", payload);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Saved",
          text: response.data.message,
          timer: 1800,
          showConfirmButton: false,
        });
        setShowModal(false);
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to save role.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    const result = await Swal.fire({
      title: "Delete Role?",
      text: `Delete ${role.name}? Assigned employees will lose this access role.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/roles-permissions/roles/${role.id}`);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: response.data.message,
          timer: 1800,
          showConfirmButton: false,
        });
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete role.",
      });
    }
  };

  const columns = [
    { header: "Role Name", key: "name" },
    { header: "Description", render: (role) => role.description || "-" },
    { header: "Permissions", render: (role) => role.permissions?.length || 0 },
    { header: "Employees", key: "users_count" },
    {
      header: "Action",
      render: (role) => (
        <div className="roles-action-group">
          <button
            className="roles-action-button edit"
            onClick={() => openEditModal(role)}
            type="button"
          >
            Edit
          </button>
          <button
            className="roles-action-button delete"
            onClick={() => handleDelete(role)}
            type="button"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="roles-permissions-page">
        <div className="roles-header-card">
          <div className="roles-header-copy">
            <span className="roles-eyebrow">Company Access Control</span>
            <h2>Roles & Permissions</h2>
            <p>Create company-specific roles and assign feature access to employees.</p>
          </div>
          <button className="roles-primary-button" onClick={openCreateModal} type="button">
            Create Role
          </button>
        </div>

        <div className="roles-table-card">
          <CommonTable
            columns={columns}
            data={roles}
            loading={loading}
            wrapperClassName="roles-table-wrapper"
            tableClassName="common-table roles-table"
            toolbarClassName="common-table-toolbar roles-table-toolbar"
            searchBoxClassName="common-table-search roles-table-search"
            emptyMessage="No roles created yet."
          />
        </div>
      </div>

      {showModal && (
        <CommonModal
          onClose={() => setShowModal(false)}
          title={editingRole ? "Edit Role" : "Create Role"}
          contentClassName="common-modal-content roles-modal-content"
          bodyClassName="common-modal-body roles-modal-body"
        >
          <div className="roles-modal-form">
            <div className="roles-form-grid">
              <div className="roles-form-group">
                <label>Role Name</label>
                <input
                  type="text"
                  className="roles-form-control"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="roles-form-group roles-form-group-full">
                <label>Description</label>
                <textarea
                  className="roles-form-control roles-form-textarea"
                  rows="3"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="roles-permission-panel">
              <div className="roles-panel-header">
                <div>
                  <h4>Permissions</h4>
                  <p>Select the features this role can access.</p>
                </div>
                <span className="roles-selected-chip">
                  {formData.permissions.length} selected
                </span>
              </div>

              <div className="roles-permission-groups">
                {catalog.length === 0 ? (
                  <div className="roles-permission-empty">
                    Permission catalog is empty. Please refresh the page after backend config is reloaded.
                  </div>
                ) : (
                  catalog.map((group) => (
                    <div key={group.module} className="roles-permission-group">
                      <div className="roles-group-heading">
                        <h5>{group.module}</h5>
                        <span>{(group.permissions || []).length} permissions</span>
                      </div>

                      <div className="roles-permission-grid">
                        {(group.permissions || []).map((permission) => (
                          <label key={permission.key} className="roles-permission-card">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.key)}
                              onChange={() => handleCheckboxChange(permission.key)}
                            />
                            <span className="roles-permission-content">
                              <strong>{permission.label}</strong>
                              <small>{permission.description}</small>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <CommonFormActions
              className="roles-form-actions"
              cancelClassName="roles-secondary-button"
              submitClassName="roles-primary-button"
              onCancel={() => setShowModal(false)}
              onSubmit={handleSave}
              submitLabel={saving ? "Saving..." : "Save Role"}
              cancelLabel="Cancel"
              submitDisabled={saving}
            />
          </div>
        </CommonModal>
      )}
    </MainLayout>
  );
}

export default RolesPermissions;
